import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { MeasuringUnitDisplay } from '../../api/models';
import AuthLayout from '../../components/AuthLayout';
import AppButton from '../../components/common/AppButton/AppButton';
import AppFormField from '../../components/common/AppFormField';
import Stepper from '../../components/common/Stepper';
import { Divider } from '../../components/subscription/Divider';
import { useAuth } from '../../context/AuthContext';
import { useSignOut } from '../../hooks/useSignOut';
import { getDefaultTimeZone, getTimezoneOptions } from '../../utils/dateTime';

const timezoneOptions = getTimezoneOptions();

const companySetupSchema = yup.object({
  companyName: yup
    .string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters'),
  companyEmail: yup
    .string()
    .required('Company email is required')
    .email('Please enter a valid company email address'),
  companyRegistrationNumber: yup
    .string()
    .required('Company registration number is required'),
  companyPhone: yup
    .string()
    .default('')
    .test(
      'min-length',
      'Please enter a valid phone number',
      (value) => !value || value.length >= 10
    ),
  companyAddress: yup.string().default(''),
  measurements: yup
    .string()
    .oneOf(['imperial', 'metric'] as const)
    .default('imperial'),
  timezone: yup.string().default(getDefaultTimeZone()),
});

type CompanySetupFormValues = yup.InferType<typeof companySetupSchema>;

const CompanySetupPage = () => {
  const { createCompany } = useAuth();
  const navigate = useNavigate();
  const { signOut, signingOut } = useSignOut();

  const methods = useForm<CompanySetupFormValues>({
    defaultValues: {
      companyName: '',
      companyEmail: '',
      companyPhone: '',
      companyAddress: '',
      companyRegistrationNumber: '',
      measurements: 'imperial',
      timezone: getDefaultTimeZone(),
    },
    resolver: yupResolver(companySetupSchema),
  });

  const { handleSubmit, formState } = methods;
  const isSubmitting = formState.isSubmitting;

  const onSubmit = async (data: CompanySetupFormValues) => {
    const createCompanyPromise = createCompany({
      name: data.companyName,
      email: data.companyEmail,
      registrationNumber: data.companyRegistrationNumber,
      phone: data.companyPhone || undefined,
      address: data.companyAddress || undefined,
      timezone: data.timezone,
      measurements: data.measurements,
    });

    try {
      await toast.promise(createCompanyPromise, {
        loading: 'Setting up your company...',
        success: `🎉 Company created successfully!`,
        error: 'Failed to create company. Please try again.',
      });

      navigate('/onboarding/subscription', { replace: true });
    } catch {
      // toast.promise already shows the error toast
    }
  };

  const subtitle = (
    <h3 className="text-base font-normal text-dark">
      Tell us about your pool service business. You can always update these
      details later.
    </h3>
  );

  return (
    <AuthLayout
      title="Set up Your Company"
      subtitle={subtitle}
      stepper={
        <Stepper steps={['Set up your company', 'Subscribe']} currentStep={1} />
      }
      multiColumn={true}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6 md:gap-8 sm:min-w-[480px] sm:max-lg:px-12 sm:max-lg:py-2"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AppFormField<CompanySetupFormValues>
              name="companyName"
              label="Company Name"
              placeholder="ABC Pool Services"
            />

            <AppFormField<CompanySetupFormValues>
              name="companyRegistrationNumber"
              label="Company Registration Number"
              placeholder="e.g. 12345678"
            />

            <div className="col-span-1 lg:col-span-2">
              <AppFormField<CompanySetupFormValues>
                name="companyEmail"
                label="Company Email"
                type="email"
                placeholder="contact@abcpools.com"
              />
            </div>

            <Divider
              label="Optional Details"
              className="col-span-1 lg:col-span-2"
            />

            <AppFormField<CompanySetupFormValues>
              name="companyAddress"
              label="Company Address"
              placeholder="123 Main St, City, State 12345"
            />

            <AppFormField<CompanySetupFormValues>
              name="companyPhone"
              label="Company Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
            />

            <AppFormField<CompanySetupFormValues>
              label="Measurement System"
              name="measurements"
              fieldType="tabs"
              size="compact"
              options={[
                { value: 'imperial', label: MeasuringUnitDisplay.imperial },
                { value: 'metric', label: MeasuringUnitDisplay.metric },
              ]}
            />

            <AppFormField<CompanySetupFormValues>
              name="timezone"
              fieldType="select"
              label="Time zone"
              options={timezoneOptions}
            />
          </div>

          <div className="flex flex-col gap-6 md:gap-8 px-8">
            <AppButton type="submit" fullWidth loading={isSubmitting}>
              Create Company
            </AppButton>

            <AppButton
              type="button"
              fullWidth
              variant="tertiary"
              loading={signingOut}
              disabled={isSubmitting || signingOut}
              onClick={signOut}
            >
              Log out
            </AppButton>
          </div>
        </form>
      </FormProvider>
    </AuthLayout>
  );
};

export default CompanySetupPage;
